export type VerificationStatus =
  | 'unverified'
  | 'missing_documents'
  | 'pending_review'
  | 'rejected'
  | 'verified';

export type UserVerificationStatus =
  | 'not_submitted'
  | 'pending'
  | 'in_review'
  | 'approved'
  | 'rejected'
  | 'expired';

export type VerificationType =
  | 'document'
  | 'course'
  | 'certification'
  | 'training'
  | 'medical_exam'
  | 'background_check';

export interface VerificationSummary {
  status: VerificationStatus;
  global: VerificationCount;
  company: VerificationCount;
  overall: OverallCount;
  canWork: boolean;
}

export interface VerificationCount {
  total: number;
  required: number;
  submitted: number;
  approved: number;
  rejected: number;
  pending: number;
  missing: number;
}

export interface OverallCount {
  totalRequired: number;
  totalApproved: number;
  totalRejected: number;
  totalPending: number;
  totalMissing: number;
  percentComplete: number;
}

/**
 * The CompanyVerification template — what a company *requires* of its
 * workforce, not what a specific user has *submitted*. Distinct from
 * `VerificationItem` (which carries `userStatus` and the per-user
 * submission metadata).
 */
export interface CompanyVerification {
  _id: string;
  name: string;
  description?: string;
  type: VerificationType;
  isRequired: boolean;
  validityPeriod?: number;
  category?: string;
  order?: number;
  company?: string;
  companyName?: string;
  isActive?: boolean;
  /**
   * Who the requirement applies to:
   *   - `internal` — only colaboradores employed directly by this company
   *   - `provider` — only colaboradores coming via a proveedor relationship
   *   - `both`     — everyone (default; matches legacy behavior)
   * Missing field reads as `'both'`.
   */
  appliesTo?: 'internal' | 'provider' | 'both';
}

export interface VerificationSubmissionHistoryEntry {
  submittedAt?: string;
  fileKey?: string;
  fileName?: string;
  status: UserVerificationStatus | 'in_review';
  submittedBy?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
}

export interface VerificationItem {
  _id: string;
  name: string;
  description?: string;
  type: VerificationType;
  isRequired: boolean;
  /** Days the approval stays valid. Mirrors `CompanyVerification.validityPeriod`
   *  on the parent rule — the rollup projection carries it through so the
   *  review modal can suggest a default expiry date. */
  validityPeriod?: number;
  userStatus: UserVerificationStatus;
  userVerificationId?: string;
  fileKey?: string;
  fileName?: string;
  /**
   * Present when this row was stamped via a bulk verification submission
   * (a planilla, CCSS report, or similar shared document covering many
   * users at once). The original supporting document is intentionally
   * not visible to the individual user. The dashboard swaps the "view
   * document" affordance for an explanatory dialog when this is set.
   */
  bulkSubmissionId?: string;
  submittedDate?: string;
  approvedDate?: string;
  rejectedDate?: string;
  rejectionReason?: string;
  expiryDate?: string;
  company?: string;
  companyName?: string;
  /** Raw ObjectId — used by the detail modal as fallback "actor" hint. */
  submittedBy?: string;
  approvedBy?: string;
  rejectedBy?: string;
  /** Prior submission/review snapshots. Drives the per-verification
   *  history modal in the unified rollup table. */
  submissionHistory?: VerificationSubmissionHistoryEntry[];
}

export interface PendingVerification {
  _id: string;
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    cedula?: string;
  };
  verification: {
    _id: string;
    name: string;
    type: string;
    description?: string;
  };
  status: string;
  fileKey: string;
  fileName: string;
  fileMimeType: string;
  fileSize: number;
  submittedAt: string;
  company?: {
    _id: string;
    name: string;
  };
  verificationType: 'company';
}

export interface PendingUser {
  _id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  cedula?: string;
  pendingCount: number;
  lastSubmission: string;
}

// File upload types
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const ALLOWED_PDF_TYPE = 'application/pdf';
export const ALLOWED_FILE_TYPES = [...ALLOWED_IMAGE_TYPES, ALLOWED_PDF_TYPE];
export const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
export const MAX_PDF_SIZE = 25 * 1024 * 1024; // 25MB

export const getMaxFileSize = (mimeType: string): number => {
  if (mimeType === ALLOWED_PDF_TYPE) {
    return MAX_PDF_SIZE;
  }
  return MAX_IMAGE_SIZE;
};

export const isValidFileType = (mimeType: string): boolean => {
  return ALLOWED_FILE_TYPES.includes(mimeType);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Status color mappings
export const getStatusColor = (status: UserVerificationStatus): 'default' | 'warning' | 'success' | 'error' => {
  switch (status) {
    case 'not_submitted':
      return 'default';
    case 'pending':
    case 'in_review':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
    case 'expired':
      return 'error';
    default:
      return 'default';
  }
};

export const getStatusLabel = (status: UserVerificationStatus): string => {
  switch (status) {
    case 'not_submitted':
      return 'Sin enviar';
    case 'pending':
      return 'Pendiente';
    case 'in_review':
      return 'En revisión';
    case 'approved':
      return 'Aprobado';
    case 'rejected':
      return 'Rechazado';
    case 'expired':
      return 'Expirado';
    default:
      return status;
  }
};

export const getOverallStatusColor = (status: VerificationStatus): 'default' | 'warning' | 'success' | 'error' | 'info' => {
  switch (status) {
    case 'unverified':
      return 'default';
    case 'missing_documents':
      return 'warning';
    case 'pending_review':
      return 'info';
    case 'rejected':
      return 'error';
    case 'verified':
      return 'success';
    default:
      return 'default';
  }
};

export const getOverallStatusLabel = (status: VerificationStatus): string => {
  switch (status) {
    case 'unverified':
      return 'Sin verificar';
    case 'missing_documents':
      return 'Documentos faltantes';
    case 'pending_review':
      return 'En revisión';
    case 'rejected':
      return 'Rechazado';
    case 'verified':
      return 'Verificado';
    default:
      return status;
  }
};

export const getVerificationTypeLabel = (type: VerificationType): string => {
  switch (type) {
    case 'document':
      return 'Documento';
    case 'course':
      return 'Curso';
    case 'certification':
      return 'Certificación';
    case 'training':
      return 'Capacitación';
    case 'medical_exam':
      return 'Examen médico';
    case 'background_check':
      return 'Verificación de antecedentes';
    default:
      return type;
  }
};
